"use client";

import { cn } from "@v1/ui/utils";
import {
  type PointerEvent,
  type ReactElement,
  useCallback,
  useRef,
  useState,
} from "react";
import {
  isTapPointer,
  shouldIgnoreClose,
  shouldOpenOnTapRelease,
} from "./disabled-reason-tap";
import { MiniHintTooltip } from "./mini-hint-tooltip";

interface DisabledReasonTooltipProps {
  readonly reason: string | null;
  readonly children: ReactElement;
  /** Wrapper classes, e.g. `flex w-full` for a full-width select. */
  readonly className?: string;
  /** In menus: `left`, so the hint never covers the row above. */
  readonly side?: "top" | "right" | "bottom" | "left";
  /** Hint classes, e.g. `z-[1100]` above the messenger window. */
  readonly contentClassName?: string;
}

/**
 * A control locked by circumstances shows why on hover, or on tap (touch).
 * No reason → the control as is.
 */
export function DisabledReasonTooltip(
  props: DisabledReasonTooltipProps,
): ReactElement {
  if (props.reason === null || props.reason.length === 0) return props.children;
  return <ReasonHint {...props} reason={props.reason} />;
}

/** Own component: hint state resets whenever the reason goes away. */
function ReasonHint(
  props: DisabledReasonTooltipProps & { readonly reason: string },
): ReactElement {
  const [open, setOpen] = useState(false);
  const openedByTapAtRef = useRef<number | null>(null);
  const wasOpenOnPressRef = useRef(false);

  const handleOpenChange = useCallback((nextOpen: boolean): void => {
    const ignore = shouldIgnoreClose({
      nextOpen,
      now: performance.now(),
      openedByTapAt: openedByTapAtRef.current,
    });
    if (!ignore) setOpen(nextOpen);
  }, []);

  // Wrapper, not the control: a disabled control receives no pointer events.
  const handlePointerDown = (event: PointerEvent<HTMLSpanElement>): void => {
    if (isTapPointer(event.pointerType)) wasOpenOnPressRef.current = open;
  };
  const handlePointerUp = (event: PointerEvent<HTMLSpanElement>): void => {
    const openNow = shouldOpenOnTapRelease({
      pointerType: event.pointerType,
      wasOpenOnPress: wasOpenOnPressRef.current,
    });
    wasOpenOnPressRef.current = false;
    if (!openNow) return;
    openedByTapAtRef.current = performance.now();
    setOpen(true);
  };

  return (
    <MiniHintTooltip
      content={props.reason}
      side={props.side ?? "top"}
      className={props.contentClassName}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <span
        className={cn("inline-flex", props.className)}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
      >
        {props.children}
      </span>
    </MiniHintTooltip>
  );
}
